import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, Menu, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import { Logo } from "./Logo";
import { Avatar } from "./Bits";
import { ACCOUNT_LINKS, AccountMenu, useSignOut } from "./AccountMenu";

const NAV = [
  { to: "/", mobileTo: "/?view=map", label: "Explore the map", end: true },
  { to: "/proposals", label: "All proposals" },
  { to: "/about", label: "How it works" },
];

const navStyle = ({ isActive }) => ({
  color: isActive ? "#C8102E" : "#14232a", textDecoration: "none", padding: "10px 0", fontWeight: isActive ? 700 : 500,
});

const MobileAccount = ({ user, close }) => {
  const signOut = useSignOut();
  return (
    <div style={{ borderTop: "1px solid var(--border)", marginTop: 12, paddingTop: 12 }} data-testid="mobile-account-section">
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 12px 10px" }}>
        <Avatar text={user.initials} size={34} />
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--ink)" }}>{user.public_name}</p>
          <p className="meta" style={{ margin: 0, fontSize: 12 }}>{user.role === "admin" ? "Admin · also a member" : "Your account"}</p>
        </div>
      </div>
      {ACCOUNT_LINKS.map(({ to, label, Icon, id }) => (
        <NavLink key={to} to={to} end={to === "/dashboard"} className="nav-a" onClick={close} data-testid={`mobile-nav-${id}`}><Icon size={18} />{label}</NavLink>
      ))}
      {user.role === "admin" && <NavLink to="/admin" className="nav-a" style={{ color: "#C8102E" }} onClick={close} data-testid="mobile-nav-admin"><ShieldCheck size={18} />Admin panel</NavLink>}
      <button type="button" className="nav-a" style={{ width: "100%", border: "none", background: "transparent", color: "var(--muted)", font: "500 16px Inter, sans-serif", cursor: "pointer" }}
        onClick={() => { close(); signOut(); }} data-testid="mobile-signout-btn"><LogOut size={18} />Sign out</button>
    </div>
  );
};

const useScrolled = (on, threshold = 12) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    if (!on) return undefined;
    const update = () => setScrolled(window.scrollY > threshold);
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, [on, threshold]);
  return on && scrolled;
};

const overlay = (scrolled) => ({
  position: "fixed", top: 0, left: 0, right: 0, zIndex: 40,
  background: scrolled ? "rgba(255,255,255,.94)" : "transparent",
  backdropFilter: scrolled ? "blur(12px)" : "none", WebkitBackdropFilter: scrolled ? "blur(12px)" : "none",
  borderBottom: `1px solid ${scrolled ? "var(--border)" : "transparent"}`,
  boxShadow: scrolled ? "0 6px 20px rgba(20,35,42,.06)" : "none",
  transition: "background-color .25s ease, box-shadow .25s ease, border-color .25s ease",
});

const MobileHeader = ({ user, transparent, scrolled }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <header data-testid="site-header" data-scrolled={transparent ? String(scrolled) : undefined} style={{ height: 56, background: "#fff", borderBottom: "1px solid var(--border)", display: "flex",
      alignItems: "center", justifyContent: "space-between", padding: "0 8px 0 16px", position: "relative", zIndex: 20, ...(transparent ? overlay(scrolled) : {}) }}>
      <Link to="/" aria-label="ReimagineCork home" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }} data-testid="header-logo">
        <Logo size={30} text={19} />
      </Link>
      <button type="button" aria-label="Menu" className="icon-btn" style={{ border: "none", background: "transparent", display: "inline-flex", alignItems: "center", gap: 8, width: "auto", padding: "0 8px" }}
        onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
        {user && <Avatar text={user.initials} size={28} />}
        <Menu size={22} />
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" style={{ width: 300, overflowY: "auto" }} data-testid="mobile-menu">
          <SheetTitle className="head" style={{ fontSize: 22 }}>Menu</SheetTitle>
          <nav aria-label="Main" style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 20 }}>
            {NAV.map((l) => (
              <NavLink key={l.to} to={l.mobileTo || l.to} end={l.end} className="nav-a" onClick={() => setOpen(false)}>{l.label}</NavLink>
            ))}
            <button type="button" className="btn btn-primary btn-md" style={{ marginTop: 12 }}
              onClick={() => { setOpen(false); navigate("/new"); }} data-testid="mobile-start-proposal">
              Start a proposal
            </button>
            {user ? <MobileAccount user={user} close={() => setOpen(false)} /> : (
              <NavLink to="/signin" className="nav-a" style={{ marginTop: 12 }} onClick={() => setOpen(false)} data-testid="mobile-signin-link">Sign in</NavLink>
            )}
          </nav>
        </SheetContent>
      </Sheet>
    </header>
  );
};

export const SiteHeader = ({ width = "wrap", white = false, transparent = false }) => {
  const { user } = useAuth();
  const mobile = useIsMobile();
  const scrolled = useScrolled(transparent);

  if (mobile) return <MobileHeader user={user} transparent={transparent} scrolled={scrolled} />;

  return (
    <header data-testid="site-header" data-scrolled={transparent ? String(scrolled) : undefined} style={{ borderBottom: "1px solid var(--border)", background: white ? "#fff" : undefined, ...(transparent ? overlay(scrolled) : {}) }}>
      <div className={width} style={{ paddingTop: 12, paddingBottom: 12, minHeight: 72, display: "flex", flexWrap: "wrap", alignItems: "center", gap: "12px 32px" }}>
        <Link to="/" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }} aria-label="ReimagineCork home" data-testid="header-logo"><Logo /></Link>
        <nav aria-label="Main" style={{ display: "flex", flexWrap: "wrap", gap: "4px 28px", flex: "1 1 auto", fontSize: 15 }}>
          {NAV.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} style={navStyle} data-testid={`nav-${l.to.replace("/", "") || "map"}`}>{l.label}</NavLink>
          ))}
        </nav>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" className="btn btn-dark btn-sm" data-testid="nav-admin" style={{ borderRadius: 999 }}><ShieldCheck size={16} />Admin panel</Link>
              )}
              <AccountMenu />
            </>
          ) : (
            <>
              <Link to="/signin" data-testid="header-signin-link" style={{ color: "#14232a", textDecoration: "none", fontWeight: 500, fontSize: 15, padding: "10px 0" }}>Sign in</Link>
              <Link to="/new" className="btn btn-primary" data-testid="header-start-proposal">Start a proposal</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export const SiteFooter = ({ width = "wrap" }) => (
  <footer style={{ borderTop: "1px solid var(--border)", background: "var(--soft)" }} data-testid="site-footer">
    <div className={width} style={{ paddingTop: 32, paddingBottom: 32, display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 16, fontSize: 14, color: "var(--muted)" }}>
      <span>ReimagineCork · Your city. Your ideas.</span>
      <span style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
        <Link to="/about" style={{ color: "var(--muted)" }}>About</Link>
        <Link to="/guidelines" style={{ color: "var(--muted)" }}>Community guidelines</Link>
        <Link to="/privacy" style={{ color: "var(--muted)" }}>Privacy</Link>
        <Link to="/contact" style={{ color: "var(--muted)" }}>Contact</Link>
      </span>
    </div>
  </footer>
);
